import { useTranslation } from 'react-i18next';
// TYPE-ONLY: these are TypeBox schema objects, and a value import would ship the
// library to a browser that never validates with it.
import type { Static } from '@b1fr0st/contracts/common';
import type { FinanceRequestSource } from '@b1fr0st/contracts/domain/finance-list';
import { statusBadge, type StatusTone } from '@/components/data-table/columns-helper';

/** The values svc-finance can derive, named once on the server. */
type RequestSource = Static<typeof FinanceRequestSource>;

/**
 * How a deposit or a payout arrived, as ONE badge both finance queues render.
 *
 * Derived by svc-finance per row (`repo/request-source.ts`), never a column —
 * so the operator reads what evidence to expect, or who entered the record,
 * before opening it.
 *
 * `auto` is a deposit made from a bank statement (`statementId` set), by the
 * matcher or by an operator binding one.
 *
 * `satisfies` rather than a bare annotation: it keeps the literal keys, so a
 * value the server adds and this map forgets is a compile error here rather than
 * a grey badge on a money queue.
 */
const SOURCE_TONE = {
  slip: 'info',
  manual: 'warning',
  auto: 'success',
  none: 'default',
} satisfies Record<RequestSource, StatusTone>;

/**
 * Which queue is asking, because `none` means two different things.
 *
 * On a deposit it is "the player filed it and attached nothing", and the label
 * says so — there is evidence to chase. On a payout nobody attaches evidence to
 * a REQUEST at all, so the only reading left is "the player asked for it
 * themselves", which is a different sentence and therefore a different key.
 * Every other value shares the deposit side's copy verbatim.
 */
type Queue = 'deposit' | 'withdraw';

export function RequestSourceBadge({ source, queue }: { source: RequestSource; queue: Queue }) {
  const { t } = useTranslation();
  const label =
    queue === 'withdraw' && source === 'none'
      ? t('finance.sourceType.player')
      : t(`finance.sourceType.${source}`, source);
  return statusBadge(label, SOURCE_TONE[source] ?? 'default');
}
